import { useEffect, useState } from 'react'
import { adminApi } from '@/services/adminApi'
import { Button } from '@/components/ui/button'
import { Save, Database } from 'lucide-react'
import { toast } from 'sonner'
import type { IngestionScopeConfig } from '@/types/admin'

const DEFAULT_CONFIG: IngestionScopeConfig = {
  enabled: true,
  days: 30,
  updatedAt: null,
  updatedBy: null,
}

export default function IngestionScopeSettingsPage() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [config, setConfig] = useState<IngestionScopeConfig>(DEFAULT_CONFIG)

  useEffect(() => {
    adminApi
      .getIngestionScopeConfig()
      .then(setConfig)
      .catch((e: any) =>
        toast.error(
          e?.response?.data?.error || e?.message || 'Failed to load ingestion scope config',
        ),
      )
      .finally(() => setLoading(false))
  }, [])

  const handleSave = async () => {
    if (!Number.isInteger(config.days) || config.days < 1 || config.days > 3650) {
      toast.error('days must be an integer between 1 and 3650')
      return
    }
    setSaving(true)
    try {
      const updated = await adminApi.updateIngestionScopeConfig({
        enabled: config.enabled,
        days: config.days,
      })
      setConfig(updated)
      toast.success('Ingestion scope config saved')
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || 'Failed to save config')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="h-6 w-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Ingestion Scope</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Caps initial connector sync to recent data on launch. Flip off to restore
              legacy &ldquo;fetch everything&rdquo; behavior. Per-connector admin actions can
              still trigger a full older-history backfill regardless of this setting.
              {config.updatedAt &&
                ` · updated ${new Date(config.updatedAt).toLocaleString()}`}
            </p>
          </div>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          size="sm"
          className="flex items-center gap-1.5 shrink-0"
        >
          <Save className="h-3.5 w-3.5" />
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      <div className="border border-gray-200 rounded-lg p-6 space-y-5 bg-white">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="text-sm font-medium text-gray-900">Enable 30-day mode</div>
            <p className="text-xs text-gray-500 mt-1 max-w-md">
              When on, new connector syncs only fetch items newer than the configured
              window. Honored by: Gmail, Google Drive, Slack, Notion, Jira. Other
              connectors (Microsoft, GitHub, Hubspot, etc.) ignore the flag and sync
              everything.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={config.enabled}
            onClick={() => setConfig(prev => ({ ...prev, enabled: !prev.enabled }))}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              config.enabled ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              aria-hidden="true"
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                config.enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700 w-32">Window</span>
          <input
            type="number"
            min={1}
            max={3650}
            value={config.days}
            onChange={e => {
              const num = parseInt(e.target.value, 10)
              if (!isNaN(num)) setConfig(prev => ({ ...prev, days: num }))
            }}
            disabled={!config.enabled}
            className="w-24 px-2 py-1.5 text-sm text-center border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
          />
          <span className="text-xs text-gray-500">
            days — items older than this are skipped on initial sync
          </span>
        </div>

        {config.updatedBy && (
          <div className="text-xs text-gray-400 pt-2 border-t border-gray-100">
            Last edited by <span className="font-mono">{config.updatedBy}</span>
          </div>
        )}
      </div>

      <div className="border border-blue-100 bg-blue-50 rounded-lg p-4 text-sm text-blue-900">
        <div className="font-medium mb-1">Triggering a full backfill</div>
        <p className="text-xs text-blue-800">
          To pull older history for a specific user&rsquo;s connector, go to{' '}
          <span className="font-mono">/admin/connectors</span> and click the
          <span className="font-mono"> Backfill</span> action on the connector row.
          That bypasses this setting and enqueues a sync with{' '}
          <span className="font-mono">older_than:{config.days}d</span>.
        </p>
      </div>
    </div>
  )
}
